import { FeatureFlagsRulesServiceBase, HttpService, LocalContext, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, SaleDocumentItemModel, SalePatternGetDocumentDTO, SalePatternModel, SalePatternSchedule, SalePatternScheduleRepetitionSkip, SalePatternUpdateDocumentDTO, monthlyScheduleKinds } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentItemModel } from '../common/serialziation-utils';
import { ProductJpkVatGroupCollectionQuery } from '../product/queries/product-jpk-vat-group.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { SaleJpkVatGroupCollectionQuery } from './queries/sale-jpk-vat-group.query-base';
import { SalePatternBO } from './sale-pattern.bo';

@Repository({
    boType: SalePatternBO,
    apiController: TaggedUrl.join('documentTemplates', [Tags.Api]),
    dto: {
        get: SalePatternGetDocumentDTO,
        update: SalePatternUpdateDocumentDTO
    }
})
export class SalePatternRepositoryBase extends RepositoryBase<SalePatternBO, SalePatternModel, SalePatternGetDocumentDTO, SalePatternUpdateDocumentDTO> {
    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private productRepository: ProductRepositoryContract,
        private clientRepository: ClientRepositoryBase,
        private readonly queryBus: QueryBus,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(http);
    }

    protected createBO(): SalePatternBO {
        return new SalePatternBO(this.weaver, this.productRepository, this.clientRepository, this.featureFlagsRulesService, this.queryBus);
    }

    public async activateAsync(...ids: number[]): Promise<{ id: number; version: string; nextIssuanceDate: Date; }[]> {
        const { data } = await this.$http.put(`${this.config.apiController}/batch/activate`, {
            ids: ids
        });
        return data;
    }

    public async deactivateAsync(...ids: number[]): Promise<{ id: number; version: string; }[]> {
        const { data } = await this.$http.put(`${this.config.apiController}/batch/deactivate`, {
            ids: ids
        });
        return data;
    }

    public async fromPlainObjectAsync(item: SalePatternGetDocumentDTO): Promise<SalePatternModel> {
        const document = new SalePatternModel(pickBy<Partial<SalePatternModel>>({
            id: item.id,
            version: item.version,

            active: item.active,
            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,

            calculationMethod: item.calculationMethod,
            clientEmails: item.clientEmails,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            dueDateInDays: item.dueDateDays,
            grossValue: item.grossValue,

            issuer: item.issuer,
            issuerUserId: item.issuerUserId,
            issueDay: item.issueDay,
            issueMonth: item.issueMonth,
            issuedByVatPayer: item.issuedByVatPayer,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            issueInBusinessDay: item.issueInBusinessDay,
            issueInBusinessDayMode: item.issueInBusinessDayMode,
            issuingStartDate: item.issuingStartDate,
            issuingEndDate: item.issuingEndDate,
            invoiceToReceipt: !!item.invoiceToReceipt,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            repetitionSkip: (item.documentTemplateSchedule === SalePatternSchedule.Monthly) && !item.repetitionSkip ? 1 : item.repetitionSkip,
            schedule: this.toModelSchedule(item.documentTemplateSchedule, item.repetitionSkip as unknown as SalePatternScheduleRepetitionSkip),

            mailTemplateId: item.emailPatternId,
            name: item.name,
            numberingId: item.numberingId,
            note: item.note,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            sendDocumentToClient: item.sendDocumentToClient,
            sendDocumentCopyToIssuer: item.sendDocumentCopyToIssuer,
            sendEmailToIssuer: item.sendEmailToIssuer,
            splitPayment: item.splitPayment,
            autoPayment: item.autoPayment,
            transactionLocation: item.transactionLocation,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            ksefSendOption: item.ksefSendOption,
            rateNP: item.rateNP,
            reverseCharge: item.reverseCharge
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId,
            name: item.deliveryAddress.name,
            line1: item.deliveryAddress.address,
            zipCode: item.deliveryAddress.zipCode
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleDocumentItemModel(pickBy<Partial<SaleDocumentItemModel>>({
                ordinalNumber: documentItem.no,
                id: documentItem.id,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                note: documentItem.note,
                includesInventoryControl: documentItem.includesInventoryControl,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,

                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,

                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                cnCode: documentItem.cnCode,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                quantity: documentItem.quantity,
                unitId: documentItem.unitId,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        return document;
    }

    protected async preparePlainObjectAsync(model: SalePatternModel, ctx: LocalContext): Promise<SalePatternUpdateDocumentDTO> {
        const jpkVatDocumentGroupCodes = await ctx.getAsync(SaleJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));
        const jpkVatProductGroupCodes = await ctx.getAsync(ProductJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));

        const item: SalePatternUpdateDocumentDTO = {
            id: model.id,
            version: model.version,

            active: model.active,
            name: model.name,
            bankAccountId: model.bankAccountNumberId,
            baseCurrencyBankAccountId: model.baseCurrencyBankAccountNumberId,
            buyer: model.buyer ? serializeDocumentBuyerModel(model.buyer) : null as any,
            calculationMethod: model.calculationMethod,
            client: model.client ? serializeDocumentClientModel(model.client) : null as any,
            clientEmails: model.clientEmails,
            communityTransactionId: model.communityTransactionId,
            currencyId: model.currencyId,
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null as any,
            dueDateDays: model.dueDateInDays,
            emailPatternId: model.mailTemplateId,
            issuingStartDate: model.issuingStartDate ? getStartOfDate(model.issuingStartDate) : null as any,
            issuingEndDate: model.issuingEndDate ? getStartOfDate(model.issuingEndDate) : null as any,
            issuer: model.issuer,
            issuerUserId: model.issuerUserId,
            issueDay: model.issueDay,
            issueMonth: model.issueMonth,
            issueInBusinessDay: model.issueInBusinessDay,
            issueInBusinessDayMode: model.issueInBusinessDay ? model.issueInBusinessDayMode : null as any,
            invoiceToReceipt: !!model.invoiceToReceipt,
            items: model.items.map(serializeDocumentItemModel),
            jpkVatDocumentGroups: model.jpkVatGroups.filter(g => jpkVatDocumentGroupCodes.includes(g)),
            jpkVatProductGroups: model.jpkVatGroups.filter(g => jpkVatProductGroupCodes.includes(g)),
            schedule: (model.schedule && monthlyScheduleKinds.includes(model.schedule as any)) ? SalePatternSchedule.Monthly : model.schedule as any,
            repetitionSkip: model.repetitionSkip,
            note: model.note,
            numberingId: model.numberingId,
            sendEmailToIssuer: model.sendEmailToIssuer,
            paymentMethodId: model.paymentMethodId,
            recipient: model.recipient,
            sendDocumentToClient: model.sendDocumentToClient,
            sendDocumentCopyToIssuer: model.sendDocumentCopyToIssuer,
            splitPayment: model.splitPayment,
            autoPayment: model.autoPayment,
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName as any,
            vatReleaseReasonSource: model.vatReleaseReasonSource as any,
            ksefSendOption: model.ksefSendOption,
            rateNP: model.rateNP,
            reverseCharge: model.reverseCharge
        };
        return item;
    }

    private toModelSchedule(schedule: SalePatternSchedule, repetition: SalePatternScheduleRepetitionSkip): SalePatternSchedule {
        if (schedule !== SalePatternSchedule.Monthly) return schedule;

        const schedulesByRepetitions: Record<SalePatternScheduleRepetitionSkip, SalePatternSchedule> = {
            1: SalePatternSchedule.Monthly,
            2: SalePatternSchedule.TwoMonths,
            3: SalePatternSchedule.ThreeMonths,
            4: SalePatternSchedule.FourMonths,
            5: SalePatternSchedule.FiveMonths,
            6: SalePatternSchedule.SixMonths,
        };
        return schedulesByRepetitions[repetition] || null;
    }
}
